<template>
  <div class="flex flex-row justify-between items-end gap-4 px-4 py-2">
    <PTooltip :label="`${letterFile!.name ? letterFile!.name + ' - ' : ''}${docName}`">
      <h1 class="text-h4 lg:text-h3 xl:text-h2 font-bold line-clamp-1">
        {{ docName }}
      </h1>
    </PTooltip>

    <div class="grow flex flex-row justify-center items-center gap-2">
      <PSelect
        label="Page"
        :model-value="currentPage"
        @update:model-value="goToPage"
        :options="pageOptions"
        class="flex gap-1 items-baseline view-selector"
        hide-messages
      />
      <PSelect
        label="Zoom"
        :model-value="currentZoom"
        @update:model-value="zoomTo"
        :options="zoomOptions"
        class="flex gap-1 items-baseline view-selector"
        hide-messages
      />
    </div>

    <SaveStatus :loading="processing" />

    <PButton mode="tertiary" :to="{ name: 'form-update', params: { chrono: letterFile!.chronoNumber } }" class="h-full">
      <OhVueIcon :name="closeIcon" />
      Fermer
    </PButton>
  </div>
</template>

<script setup lang="ts">
import { useAsyncOperation } from "@/app/composable/useAsyncOperation.ts";
import type { LetterFileDocumentDto } from "@/app/dto/letter-file-document.dto.ts";
import { useLetterFileStore } from "@/app/stores/letter-file.store.ts";
import { useUserPreferencesStore } from "@/app/stores/user-preferences.store.ts";
import { FormOperation } from "@/app/types";
import SaveStatus from "@/app/views/LetterFileForm/SaveStatus.vue";
import type { Unsubscribe } from "@embedpdf/core";
import { useActiveDocument } from "@embedpdf/plugin-document-manager/vue";
import { useScroll } from "@embedpdf/plugin-scroll/vue";
import { useZoom, type ZoomLevel, ZoomMode } from "@embedpdf/plugin-zoom/vue";
import { PButton, PSelect, PTooltip, useIcons } from "@pasta/box-v2";
import { range } from "lodash";
import { OhVueIcon } from "oh-vue-icons";
import { RiCloseFill } from "oh-vue-icons/icons";
import { computed, onMounted, onUnmounted, ref } from "vue";

type SelectOption<T = unknown> = {
  label: string;
  value: T;
  [key: string]: unknown;
  children?: Omit<SelectOption, "children">[];
};

type Props = { document: LetterFileDocumentDto };
const props = defineProps<Props>();

const { activeDocument } = useActiveDocument();
const { provides: zoomScope } = useZoom(activeDocument.value!.id);
const { provides: scrollScope } = useScroll(activeDocument.value!.id);
const userPreferencesStore = useUserPreferencesStore();
const letterFileStore = useLetterFileStore();
const letterFile = computed(() => letterFileStore.getCurrent());
const asyncOperation = useAsyncOperation();
const processing = asyncOperation.getShowSaved(FormOperation.SIGN_POS);

const docName = props.document.name + props.document.extension;
const currentPage = ref<number>(1);
const pageOptions = computed(() => {
  const pageCount = activeDocument.value!.document?.pageCount ?? 0;
  return range(1, pageCount + 1).map(p => ({
    label: `${p}/${pageCount}`,
    value: p,
  }));
});

const initZoom = ref<boolean>(false);
const currentZoom = ref<ZoomLevel>();
const baseZoomOptions: SelectOption<ZoomLevel>[] = [
  {
    label: "Ajusté",
    value: ZoomMode.FitWidth,
  },
];
baseZoomOptions.push(
  ...[50, 100, 150, 200].map(level => ({
    label: `${level}%`,
    value: level / 100,
  }))
);
let zoomOptions = [...baseZoomOptions];

const [closeIcon] = useIcons(RiCloseFill);

const unsubscribes = ref<Unsubscribe[]>([]);

function goToPage(pageNumber: number) {
  scrollScope.value?.scrollToPage({ pageNumber });
}

function zoomTo(zoomLevel: ZoomLevel) {
  zoomScope.value?.requestZoom(zoomLevel, { vx: 0, vy: 0 });
}

onMounted(() => {
  if (!scrollScope.value) return;
  const offScrollChange = scrollScope.value.onPageChange(event => {
    currentPage.value = event.pageNumber;
  });
  unsubscribes.value.push(offScrollChange);
});

onMounted(() => {
  if (!zoomScope.value) return;

  const offZoomChange = zoomScope.value.onZoomChange(event => {
    if (!initZoom.value) {
      initZoom.value = true;
      zoomTo(userPreferencesStore.zoomLevel);
      return;
    }

    zoomOptions = [...baseZoomOptions];
    if (event.level === ZoomMode.FitWidth) {
      currentZoom.value = ZoomMode.FitWidth;
    } else {
      const newZoom = event.newZoom;
      if (!baseZoomOptions.some(option => option.value === newZoom)) {
        const arbitraryZoom = {
          value: newZoom,
          label: `${(newZoom * 100).toFixed()}%`,
        };
        zoomOptions = [arbitraryZoom, ...baseZoomOptions];
      }
      currentZoom.value = newZoom;
    }
    userPreferencesStore.updateMine({
      zoomLevel: typeof currentZoom.value === "string" ? null : currentZoom.value,
    });
  });
  unsubscribes.value.push(offZoomChange);
});

onUnmounted(() => {
  unsubscribes.value.forEach(unsubscribe => unsubscribe());
});
</script>

<style>
.view-selector > :not(.p-select-label) {
  width: 6rem !important;
}

.view-selector:not(:first-child) > .p-select-label {
  padding-left: 1rem;
  border-left: solid 1px var(--color-grey-900);
  padding-block: 0.5rem;
  margin-bottom: 0 !important;
}
</style>
